import { Box, Typography } from '@mui/material';
import React from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormHandleSubmit,
  UseFormRegister,
} from 'react-hook-form';

import {
  PossibleQuestionModelType,
  Question,
  QuestionSectionType,
  QuestionTopicType,
  Sector,
} from '@/domain/impact/impact.entity';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { translate } from '@/ui/i18n';
import { QuizFormSectionTitle } from '@/ui/pages/app/components/quiz-panel/co2/components/QuizFormSectionTitle';
import { DynamicFormInput } from '@/ui/pages/app/components/quiz-panel/DynamicFormInput';
import { InputContainer } from '@/ui/pages/app/components/quiz-panel/InputContainer';

import { QuizFormSeparator } from './co2/components/QuizFormSeparator';

type Inputs = { [key: string]: string };

type IProps<T extends PossibleQuestionModelType> = {
  questions: Map<Question['paramId'], Question>;
  selectedTopic: QuestionTopicType<T>;
  register: UseFormRegister<FieldValues>;
  errors: FieldErrors;
  control: Control;
  handleSubmit: UseFormHandleSubmit<Inputs, Inputs>;
  sector?: Sector;
  sections: Array<QuestionSectionType<T>>;
  questionsBySector: Record<Sector, Array<string>>;
  sectionNameFunction: (section: QuestionSectionType<T>) => string;
  useSectionTitleSeparators?: boolean;
  displayValidationButtons?: boolean;
  debugMode: boolean;
  onSubmitInput: (data: Inputs, envKey: string) => void;
};

export const QuizLayout = <T extends PossibleQuestionModelType>({
  questions,
  selectedTopic,
  register,
  errors,
  control,
  handleSubmit,
  sector,
  sections,
  questionsBySector,
  sectionNameFunction,
  useSectionTitleSeparators,
  displayValidationButtons,
  debugMode,
  onSubmitInput,
}: IProps<T>) => {
  const getQuestionsBySection = (section: string): Array<Question> => {
    const questionsForSection: Array<Question> = [];
    for (const question of questions.values()) {
      if (
        question.topic === selectedTopic &&
        question.section === section &&
        (!sector || questionsBySector[sector].includes(question.paramId))
      ) {
        questionsForSection.push(question);
      }
    }
    return questionsForSection;
  };

  return (
    <Box>
      {sections.map((section) => (
        <Box key={section} id={`${section.toLowerCase()}-form`} sx={{ mb: 2 }}>
          {/* section title*/}
          {useSectionTitleSeparators ? (
            <QuizFormSectionTitle title={sectionNameFunction(section)} />
          ) : (
            <QuizFormSeparator label={sectionNameFunction(section)} />
          )}
          {/* questions */}
          <QuestionContainer
            questions={getQuestionsBySection(section)}
            control={control}
            errors={errors}
            handleSubmit={handleSubmit}
            register={register}
            debugMode={debugMode}
            onSubmitInput={onSubmitInput}
          />

          {displayValidationButtons && (
            <FSPrimaryButton sx={{ mt: 2 }}>
              {translate('Form.save')}
            </FSPrimaryButton>
          )}
        </Box>
      ))}
    </Box>
  );
};

function QuestionContainer({
  register,
  handleSubmit,
  errors,
  control,
  questions,
  debugMode,
  onSubmitInput,
}: {
  register: UseFormRegister<FieldValues>;
  errors: FieldErrors;
  control: Control;
  handleSubmit: UseFormHandleSubmit<Inputs, Inputs>;
  questions: Array<Question>;
  debugMode: boolean;
  onSubmitInput: (data: Inputs, envKey: string) => void;
}) {
  if (questions.length === 0) {
    return (
      <Typography sx={{ mt: 2 }}>{translate('Sector.no-question')}</Typography>
    );
  }
  const questionsElements: Array<React.JSX.Element> = [];

  for (const question of questions) {
    if (question?.isHidden) {
      continue;
    }

    questionsElements.push(
      <InputContainer
        parameters={question}
        key={`${question.section}_${question.topic}_${question.paramId}`}
        debugMode={debugMode}
      >
        <DynamicFormInput
          parameters={question}
          register={register}
          control={control}
          errors={errors}
          // --
          // // TODO : check this :
          // ca me semble louche que le code suivant ne marche pas :
          //
          // onSubmitInput={() => {
          //   handleSubmit((data: Inputs) => {
          //     onSubmitInput(data, question.paramId);
          //   });
          // }}
          //
          // je me demande si le handleSubmit est bien utilisé ici :-p
          //
          // --
          onSubmitInput={handleSubmit((data: Inputs) => {
            onSubmitInput(data, question.paramId);
          })}
        />
      </InputContainer>,
    );
  }

  return questionsElements;
}
